import { mapShareContactTypes, ShareContactTypes } from "../HeyTraderTypes/ShareContactTypes";
import { CommonSharedByType, mapCommonSharedByType } from "../Share/CommonSharedByType";
import { FSBathroom, mapFSBathrooms } from "./Bathroom/Bathroom";
import { FSDoor, mapFSDoors } from "./Door/Door";
import { FSFloorFinishing, mapFSFloorFinishing } from "./FloorFinishing/FloorFinishing";
import { FSKitchen, mapFSKitchens } from "./Kitchen/Kitchen";
import { FSLight, mapFSLights } from "./Light/Light";
import { FSOther, mapFSOthers } from "./Other/Other";
import { FSPaint, mapFSPaints } from "./Paint/Paint";
import { FSTile, mapFSTiles } from "./Tile/Tile";


/* **************************************************** Finishing Shedule Main Features */
export interface FSMainFeature {
    id: number;
    feature: string;
    status: 1 | 0;
}

export const mapFSMainFeature = (data: any): FSMainFeature => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const res: FSMainFeature = {
        id: data.id,
        feature: data.feature,
        status: data.status
    };

    return res;
};

export const mapFSMainFeatures = (data: any[]): FSMainFeature[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapFSMainFeature);
};


/* **************************************************** Finishing Shedule Feature Data */
export interface FSFeatureData {
    id: number;
    fs_feature_id: number;
    category: string;
    location_name: string | null;
    location_id: number | null;
}

export const mapFSFeatureData = (data: any): FSFeatureData => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const res: FSFeatureData = {
        id: data.id,
        fs_feature_id: data.fs_feature_id,
        category: data.category,
        location_name: data.location_name || null,
        location_id: data.location_id || null,
    };

    return res;
};

export const mapFSFeatureDatas = (data: any[]): FSFeatureData[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapFSFeatureData);
};


/* **************************************************** Finishing Shedule Feature */
export interface FSFeature {
    id: number;
    project_id: number;
    feature: 'Light' | 'Door' | 'Paint' | 'Bathroom' | 'Kitchen' | 'Tiles' | 'Floor Finishing' | 'Others';
    status: 1 | 0;
    main_feature_id: number;
    can_edit: boolean;
    shared_by: CommonSharedByType | null;
    data: FSFeatureData[];
    share_list: ShareContactTypes[];
    main_feature: FSMainFeature | null;
}

export const mapFSFeature = (data: any): FSFeature => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const res: FSFeature = {
        id: data.id,
        project_id: data.project_id,
        feature: data.feature,
        status: data.status,
        main_feature_id: data.main_feature_id,
        can_edit: data.can_edit,
        shared_by: data.shared_by ? mapCommonSharedByType(data.shared_by) : null,
        data: Array.isArray(data.data) ? mapFSFeatureDatas(data.data) : [],
        share_list: Array.isArray(data.share_list) ? mapShareContactTypes(data.share_list) : [],
        main_feature: data.main_feature ? mapFSMainFeature(data.main_feature) : null,
    };

    return res;
};

export const mapFSFeatures = (data: any[]): FSFeature[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapFSFeature);
};


/* **************************************************** Finishing Shedule Project */
export interface FSProject {
    project_id: number;
    job_number: string;
    project_type: 'CP' | 'DP';
    description: string;
    status: 'Current' | 'Ongoing' | 'Completed';
    user_id: number;
    address: string;
    post_code: string;
    is_fs_project_list: boolean;
    feature_list: FSFeature[];
    shared_by: CommonSharedByType | null;
    is_own_project: boolean;
    contact: ShareContactTypes[];
}

export const mapFSProject = (data: any): FSProject => {
    if (!data) {
        throw new Error("Invalid Data");
    }

    const res: FSProject = {
        project_id: data.project_id,
        job_number: data.job_number,
        project_type: data.project_type,
        description: data.description,
        status: data.status,
        user_id: data.user_id,
        address: data.address,
        post_code: data.post_code,
        is_fs_project_list: data.is_fs_project_list,
        feature_list: Array.isArray(data.feature_list) ? mapFSFeatures(data.feature_list) : [],
        shared_by: data.shared_by ? mapCommonSharedByType(data.shared_by) : null,
        is_own_project: data.is_own_project,
        contact: Array.isArray(data.contact) ? mapShareContactTypes(data.contact) : [],
    };

    return res;
};

export const mapFSProjects = (data: any[]): FSProject[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid Data");
    }

    return data.map(mapFSProject);
};


/* **************************************************** Finishing shedule report data */
export interface FSReportFeatureData {
    "Light"?: FSLight[];
    "Door"?: FSDoor[];
    "Paint"?: FSPaint[];
    "Bathroom"?: FSBathroom[];
    "Kitchen"?: FSKitchen[];
    "Tiles"?: FSTile[];
    "Floor Finishing"?: FSFloorFinishing | null;
    "Others"?: FSOther[];
}

export const mapFSReportFeatureData = (data: any): FSReportFeatureData => {
    if (!data || typeof data !== "object") {
        throw new Error("Invalid Data");
    }

    const result: Partial<FSReportFeatureData> = {};

    if (Array.isArray(data["Light"])) {
        result["Light"] = mapFSLights(data["Light"]);
    }

    if (Array.isArray(data["Door"])) {
        result["Door"] = mapFSDoors(data["Door"]);
    }

    if (Array.isArray(data["Paint"])) {
        result["Paint"] = mapFSPaints(data["Paint"]);
    }

    if (Array.isArray(data["Bathroom"])) {
        result["Bathroom"] = mapFSBathrooms(data["Bathroom"]);
    }

    if (Array.isArray(data["Kitchen"])) {
        result["Kitchen"] = mapFSKitchens(data["Kitchen"]);
    }

    if (Array.isArray(data["Tiles"])) {
        result["Tiles"] = mapFSTiles(data["Tiles"]);
    }

    if (data["Floor Finishing"]) {
        result["Floor Finishing"] = mapFSFloorFinishing(data["Floor Finishing"]);
    }

    if (Array.isArray(data["Others"])) {
        result["Others"] = mapFSOthers(data["Others"]);
    }

    return result as FSReportFeatureData;
};


export interface FSReportData {
    data: FSReportFeatureData | null;
    fs_report_download_path: string | null;
}


export const mapFSReportData = (reportData: any): FSReportData => {
    if (!reportData || typeof reportData !== "object") {
        throw new Error("Invalid Data");
    }

    return {
        data: reportData.data ? mapFSReportFeatureData(reportData.data) : null,
        fs_report_download_path: reportData.fs_report_download_path ?? null,
    };
};

/* **************************************************** Finishing shedule feature config option */
export interface FeatureOption {
    featureId: number;
    feature: 'Light' | 'Door' | 'Paint' | 'Bathroom' | 'Kitchen' | 'Tiles' | 'Floor Finishing' | 'Others';
    accessoryView: 1 | 0;
}